import { useState, FormEvent } from "react";
import { products } from "@/mocks/products";

interface ReviewWriteModalProps {
  onClose: () => void;
  initialProduct?: string;
}

const SUBMIT_URL = "https://readdy.ai/api/form/d7do1fk5q56ra7tufnog";

const ReviewWriteModal = ({ onClose, initialProduct }: ReviewWriteModalProps) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (rating === 0) return;
    const form = e.currentTarget;
    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    data.set("rating", String(rating));
    await fetch(SUBMIT_URL, { method: "POST", body: data });
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg overflow-hidden"
        style={{ animation: "fadeInUp 0.25s ease" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-stone-100">
          <div>
            <h2 className="text-base font-black text-stone-900">리뷰 작성하기</h2>
            <p className="text-xs text-stone-400 mt-0.5">실제 설치 경험을 공유해주세요</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer text-stone-500"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center justify-center py-14 px-6">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full mb-4">
              <i className="ri-check-line text-2xl text-emerald-600" />
            </div>
            <h3 className="text-base font-bold text-stone-900 mb-1">리뷰가 등록되었습니다!</h3>
            <p className="text-sm text-stone-500 text-center mb-6">소중한 후기 감사합니다. 검토 후 게시됩니다.</p>
            <button
              onClick={onClose}
              className="px-8 py-2.5 bg-stone-900 text-white text-sm font-semibold rounded-full hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              닫기
            </button>
          </div>
        ) : (
          <form data-readdy-form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* Product Select */}
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">설치 제품 *</label>
              <select
                name="product"
                required
                className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400 bg-white cursor-pointer"
              >
                <option value="">제품을 선택해주세요</option>
                {products.map((p) => (
                  <option key={p.id} value={p.name} selected={p.name === initialProduct}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Name + Phone */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">이름 *</label>
                <input
                  name="name"
                  required
                  placeholder="홍길동"
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">설치 지역</label>
                <input
                  name="location"
                  placeholder="예: 서울 강남구"
                  className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400 transition-colors"
                />
              </div>
            </div>

            {/* Star Rating */}
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-2">별점 *</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="w-8 h-8 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                  >
                    <i className={`text-xl ${star <= (hoverRating || rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-stone-300"}`} />
                  </button>
                ))}
                {rating > 0 && (
                  <span className="text-xs text-stone-500 ml-2">
                    {["", "별로예요", "그저 그래요", "괜찮아요", "좋아요", "최고예요"][rating]}
                  </span>
                )}
              </div>
              {rating === 0 && (
                <p className="text-[10px] text-rose-400 mt-1">별점을 선택해주세요</p>
              )}
            </div>

            {/* Review Text */}
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">
                후기 내용 * <span className="text-stone-400 font-normal">(최대 500자)</span>
              </label>
              <textarea
                name="review"
                required
                maxLength={500}
                rows={4}
                placeholder="설치 경험, 제품 품질, 서비스 등 자유롭게 작성해주세요"
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full border border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-800 outline-none focus:border-stone-400 transition-colors resize-none"
              />
              <p className="text-[10px] text-stone-400 text-right mt-0.5">{charCount} / 500</p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || rating === 0}
              className="w-full bg-stone-900 text-white text-sm font-bold py-3 rounded-xl hover:bg-stone-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "등록 중..." : "리뷰 등록하기"}
            </button>
          </form>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ReviewWriteModal;
